<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from "@/components/landingPage/ui/card";

import {
  Paintbrush,
  MessageCircle,
  TabletSmartphone,
  BadgeCheck,
  Goal,
  PictureInPicture,
  MousePointerClick,
  Newspaper,
} from "lucide-vue-next";

interface FeaturesProps {
  icon: string;
  title: string;
  description: string;
}

const featureList: FeaturesProps[] = [
  {
    icon: "tabletSmartphone",
    title: "Profil personnalisé",
    description:
      "Créez votre profil unique basé sur vos intérêts, compétences et préférences professionnelles à travers des activités ludiques.",
  },
  {
    icon: "badgeCheck",
    title: "Mini-jeux d'évaluation",
    description:
      "Découvrez vos forces à travers des jeux : scénarios, suites logiques, métiers, vitesse, compétences et environnement de travail.",
  },
  {
    icon: "goal",
    title: "Matching intelligent",
    description:
      "Algorithme IA pour recommander des emplois, formations et entreprises selon vos compétences et préférences.",
  },
  {
    icon: "pictureInPicture",
    title: "Interface gamifiée",
    description:
      "Progression motivante avec badges, quêtes et niveaux. Design adapté aux besoins sensoriels TSA pour réduire l'anxiété.",
  },
  {
    icon: "mousePointerClick",
    title: "Tableau de bord",
    description:
      "Visualisez vos progrès, objectifs et activités. Accédez à des ressources pédagogiques adaptées à votre rythme.",
  },
  {
    icon: "newspaper",
    title: "Sécurité RGPD",
    description:
      "Données chiffrées et hébergement sécurisé. Vous gardez le contrôle total sur vos informations partagées.",
  },
];

const iconMap: Record<
  string,
  | typeof TabletSmartphone
  | typeof BadgeCheck
  | typeof Goal
  | typeof PictureInPicture
  | typeof Paintbrush
  | typeof MousePointerClick
  | typeof MessageCircle
  | typeof Newspaper
> = {
  tabletSmartphone: TabletSmartphone,
  badgeCheck: BadgeCheck,
  goal: Goal,
  pictureInPicture: PictureInPicture,
  paintbrush: Paintbrush,
  mousePointerClick: MousePointerClick,
  messageCircle: MessageCircle,
  newspaper: Newspaper,
};
</script>

<template>
  <section
    id="features"
    class="container mx-auto py-24 sm:py-32"
  >
    <h2 class="text-lg text-[#ff9500] text-center mb-2 tracking-wider">
      Fonctionnalités
    </h2>

    <h2 class="text-3xl md:text-4xl text-center font-bold mb-4">
      Une plateforme complète et bienveillante
    </h2>

    <h3 class="md:w-1/2 mx-auto text-xl text-center text-muted-foreground mb-8">
      La plateforme repose sur la collecte, visualisation et analyse des données 
      issues des parcours et jeux pour créer un profil unique et évolutif.
    </h3>

    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="{ icon, title, description } in featureList"
        :key="title"
      >
        <Card class="h-full bg-background border-0 shadow-none">
          <CardHeader class="flex justify-center items-center">
            <div
              class="bg-[#ff9500]/20 p-2 rounded-full ring-8 ring-[#ff9500]/10 mb-4"
            >
              <component
                :is="iconMap[icon]"
                class="size-6 text-[#ff9500]"
              />
            </div>

            <CardTitle>
              {{ title }}
            </CardTitle>
          </CardHeader>

          <CardContent class="text-muted-foreground text-center">
            {{ description }}
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
</template>

<style lang="less" scoped></style>
